// @flow

import * as React from 'react';
import { type FileType } from './types';
import styles from './DropZone.less';

/** The method of file selection */
export type FileSelectionMethod = 'drag_drop' | 'file_picker';

/** Possible rejection reasons */
export type RejectionReason = 'wrong_file_type' | 'file_too_large';

/** The callback type to add a file */
export type AddFileCallback = (file: File, method: FileSelectionMethod) => void;

/** The callback type to be informed of a file rejection */
export type FileRejectedCallback = (file: File, reason: RejectionReason) => void;

/** Component props for a <DropZone> */
type Props = {|
  /**
   * Array of allowed filetypes for the file input component
   */
  allowedFileTypes: Array<FileType>,

  /** wether to allow the selection of multiple files via the input */
  allowMultipleSelection: boolean,

  /**
   * Add a file.
   */
  addFile: AddFileCallback,

  /**
   * If a file is dropped into the dropzone, it might be rejected if it does not meet the criteria.
   * this callback can be used to be informed of those events
   */
  fileRejected?: FileRejectedCallback,

  /**
   * It is very easy for a user to mistakenly miss the drop zone and drop the file elsewhere on the
   * page.  The default behavior is that the file will take over the page (change window.location).
   * We do not want this to happen.
   */
  disableOnDropForPage: boolean,

  /** max file size.  If the file exceeds this size it will be rejected and fileRejected() will be called */
  maxSizeInBytes?: number,

  /**
   * It's common to allow the drag/drop to happen in a larger area of the screen.  This property allows
   * a parent component to affect the styling on this component if a drag is occurring elsewhere.
   * Setting this to true changes the styling to look like the drag is over the component even though it isn't.
   */
  isDragOverOutsideComponent: boolean,

  /** the css width of the component */
  width: string,

  /** the css height of the component */
  height: string,

  /** child components */
  children: React.Node,
|};

type State = {|
  isDragOver: boolean,
|};

/**
 * Return the file extension in lower case, or undefined, if the file doesn't have an extension
 */
export const fileExtentsion = (filename: string): ?string => {
  const lastIndexOfPeriod = filename.lastIndexOf('.');
  if (lastIndexOfPeriod < 0) {
    return undefined;
  }
  return filename.substring(lastIndexOfPeriod).toLowerCase();
};

/**
 * Determine if the file should be rejected.
 * If the file should be rejected, return the reason
 * If the file is accepted, return undefined
 */
const rejectReasonForFile = (
  file: File,
  allowedFileTypes?: Array<FileType>,
  maxSizeInBytes?: number,
): ?RejectionReason => {
  if (maxSizeInBytes && file.size > maxSizeInBytes) {
    return 'file_too_large';
  }

  if (allowedFileTypes) {
    const extension = fileExtentsion(file.name);
    if (!extension) {
      return 'wrong_file_type';
    }
    const extensionIsAllowed = allowedFileTypes.includes(extension);
    if (!extensionIsAllowed) {
      return 'wrong_file_type';
    }
  }
  return undefined;
};

/**
 * This is a helper method to process file when added from a file input or a drop.
 * If the file should be rejected, we call the fileRejected callback.
 * Otherwise we call the addFile callback.
 */
export const processFile = (
  file: File,
  addFile: AddFileCallback,
  allowedFileTypes?: Array<FileType>,
  maxSizeInBytes?: number,
  fileRejected?: FileRejectedCallback,
  selectionMethod: FileSelectionMethod,
) => {
  const rejectionReason = rejectReasonForFile(file, allowedFileTypes, maxSizeInBytes);
  if (rejectionReason) {
    if (fileRejected) {
      fileRejected(file, rejectionReason);
    }
  } else {
    addFile(file, selectionMethod);
  }
};

/**
 * This helper method can be used to handle an onDrop event.  It handles the different apis for the dataTransfer
 * object in different browsers and calls back to the addFile function passed in.
 *
 * The caller is still responsible for calling e.preventDefault(), assuming that is the functionality they want.
 *
 * Share this method so it can be used from elsewhere.  It's common to allow a file drop to happen in a
 * larger area of the screen to make it easier for users.
 */
export const processFilesFromDrop = (
  e: SyntheticDragEvent<*>,
  addFile: AddFileCallback,
  allowedFileTypes?: Array<FileType>,
  maxSizeInBytes?: number,
  fileRejected?: FileRejectedCallback,
) => {
  // This is annoying, but we need to support different apis for dataTransfer
  if (e.dataTransfer.items) {
    // chrome, firefox
    for (let i = 0; i < e.dataTransfer.items.length; i++) {
      const file = e.dataTransfer.items[i].getAsFile();
      processFile(file, addFile, allowedFileTypes, maxSizeInBytes, fileRejected, 'drag_drop');
    }
  } else {
    // safari
    for (let i = 0; i < e.dataTransfer.files.length; i++) {
      const file = e.dataTransfer.files[i];
      processFile(file, addFile, allowedFileTypes, maxSizeInBytes, fileRejected, 'drag_drop');
    }
  }
};

/**
 * This is a UI to handle file drag and drop as well as file selection using a file selector.
 */
export default class DropZone extends React.Component<Props, State> {
  static defaultProps = {
    allowedFileTypes: ['*'],
    allowMultipleSelection: true,
    disableOnDropForPage: true,
    isDragOverOutsideComponent: false,
    width: '100%',
    height: '100%',
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      isDragOver: false,
    };
  }

  componentDidMount() {
    if (this.props.disableOnDropForPage) {
      // dragover and drop are both needed to make this work
      // eslint-disable-next-line no-restricted-globals
      window.addEventListener('dragover', this.eventPreventDefault);
      // eslint-disable-next-line no-restricted-globals
      window.addEventListener('drop', this.eventPreventDefault);
    }
  }

  componentWillUnmount() {
    if (this.props.disableOnDropForPage) {
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener('dragover', this.eventPreventDefault);
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener('drop', this.eventPreventDefault);
    }
  }

  fileInputRef: React.ElementRef<*>;

  eventPreventDefault = (e: Event) => {
    e.preventDefault();
  };

  handleDragOver = (e: SyntheticDragEvent<*>) => {
    e.preventDefault();
    this.setState({ isDragOver: true });
  };

  handleDragLeave = (e: SyntheticDragEvent<*>) => {
    e.preventDefault();
    this.setState({ isDragOver: false });
  };

  handleDrop = (e: SyntheticDragEvent<*>) => {
    e.preventDefault();
    processFilesFromDrop(
      e,
      this.props.addFile,
      this.props.allowedFileTypes,
      this.props.maxSizeInBytes,
      this.props.fileRejected,
    );
    this.setState({ isDragOver: false });
  };

  fileSelectionChanged = (e: SyntheticInputEvent<HTMLInputElement>) => {
    const filesInElement = e.target.files;
    Array.from(filesInElement).forEach(f =>
      processFile(
        f,
        this.props.addFile,
        this.props.allowedFileTypes,
        this.props.maxSizeInBytes,
        this.props.fileRejected,
        'file_picker',
      ),
    );
    // Clear out the value just after the selection is made, so onChange will fire again if the video is removed and re-added
    this.fileInputRef.value = '';
  };

  render = () => {
    const inputProperties = this.props.allowMultipleSelection ? { multiple: true } : {};

    return (
      <div
        style={{
          width: this.props.width,
          height: this.props.height,
        }}
        className={`${styles.dropZone} ${
          this.props.isDragOverOutsideComponent || this.state.isDragOver ? styles.isDragOver : ''
        }`}
        onDragOver={this.handleDragOver}
        onDragLeave={this.handleDragLeave}
        onDrop={this.handleDrop}
        onClick={() => this.fileInputRef.click()}
      >
        <input
          style={{ display: 'none' }}
          type="file"
          name="media"
          accept={this.props.allowedFileTypes.join(',')}
          {...inputProperties}
          onChange={this.fileSelectionChanged}
          onClick={e => e.stopPropagation()}
          ref={ref => {
            this.fileInputRef = ref;
          }}
        />
        {this.props.children}
      </div>
    );
  };
}
